import { FunnelSimple } from "@phosphor-icons/react";
import { useState } from "react";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import WorkspaceAccountSelector from "common/components/WorkspaceAccountSelector";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import CampaignSelector from "./CampaignSelector";
import LabelSelector from "./LabelSelector";

interface SelectedFilters {
  accountIds: string[];
  campaignIds: string[];
  labelIds: string[];
}

interface FilterConversationsProps {
  activeFilters: SelectedFilters;
  onSetActiveFilters: (filters: {
    accountIds: string[];
    campaignIds: string[];
    labelIds: string[];
  }) => void;
}

export default function FilterConversations({
  activeFilters,
  onSetActiveFilters,
}: FilterConversationsProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>({
    accountIds: activeFilters.accountIds,
    campaignIds: activeFilters.campaignIds,
    labelIds: activeFilters.labelIds,
  });

  function applyFilters(): void {
    const { accountIds, campaignIds, labelIds } = selectedFilters;

    onSetActiveFilters({
      accountIds,
      campaignIds,
      labelIds,
    });

    setIsDialogOpen(false);
  }

  function clearFilters(): void {
    setSelectedFilters({
      accountIds: [],
      campaignIds: [],
      labelIds: [],
    });
  }

  function handleDialogChange(isOpen: boolean): void {
    setIsDialogOpen(isOpen);

    if (isOpen) {
      setSelectedFilters({
        accountIds: activeFilters.accountIds,
        campaignIds: activeFilters.campaignIds,
        labelIds: activeFilters.labelIds,
      });
    }
  }

  const hasActiveFilters =
    activeFilters.accountIds.length > 0 ||
    activeFilters.campaignIds.length > 0 ||
    activeFilters.labelIds.length > 0;

  const isClearButtonDisabled =
    !selectedFilters.accountIds.length &&
    !selectedFilters.campaignIds.length &&
    !selectedFilters.labelIds?.length;

  const updateSelectedFilter =
    (key: keyof SelectedFilters) => (value: string[]) => {
      setSelectedFilters((prev) => ({ ...prev, [key]: value }));
    };

  const totalFilterCount =
    selectedFilters.accountIds.length +
    selectedFilters.campaignIds.length +
    (selectedFilters.labelIds?.length ?? 0);

  return (
    <Component open={isDialogOpen} onOpenChange={handleDialogChange}>
      <Trigger asChild>
        <Button
          intent="iconOnly"
          variant="outline-black"
          className="group relative"
        >
          <FunnelSimple />

          <RenderIf condition={hasActiveFilters}>
            <span className="absolute right-0 top-0 flex size-4 items-center justify-center rounded-full bg-purple-500 px-1 text-button-xxs text-white">
              {totalFilterCount}
            </span>
          </RenderIf>
        </Button>
      </Trigger>

      <Content>
        <Title className="mb-10 mt-4">Inbox filters</Title>

        <Description className="sr-only">
          Filter conversations by account, campaign, and label
        </Description>

        <div className="flex flex-col gap-4">
          <FormField
            label="Filter by account"
            htmlFor="account-select"
            size="lg"
          >
            <WorkspaceAccountSelector
              selection={selectedFilters.accountIds}
              setSelection={updateSelectedFilter("accountIds")}
            />
          </FormField>

          <FormField
            label="Filter by campaign"
            htmlFor="campaign-select"
            size="lg"
          >
            <CampaignSelector
              selectedCampaignIds={selectedFilters.campaignIds}
              setSelectedCampaignIds={updateSelectedFilter("campaignIds")}
            />
          </FormField>

          <FormField label="Filter by labels" htmlFor="label-select" size="lg">
            <LabelSelector
              selectedLabelIds={selectedFilters.labelIds}
              setSelectedLabelIds={updateSelectedFilter("labelIds")}
            />
          </FormField>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <Button
            variant="outline-black"
            size={isDesktop ? "md" : "lg"}
            onClick={clearFilters}
            disabled={isClearButtonDisabled}
          >
            Clear all filters
          </Button>

          <Button size={isDesktop ? "md" : "lg"} onClick={applyFilters}>
            Apply filters
          </Button>
        </div>
      </Content>
    </Component>
  );
}
